// src/components/TopNav.tsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useData } from "../DataContext";
import { useTranslation } from "react-i18next";

export type Role = "tenant" | "admin";

type TopNavProps = {
  isDrawerOpen: boolean;
  onToggleDrawer: () => void;
  onCloseDrawer: () => void;
  onLogout: () => void;
  authRole: Role;
};

type QuickAddItem = {
  to: string;
  icon: string;
  title: string;
  sub: string;
};

export default function TopNav({
  isDrawerOpen,
  onToggleDrawer,
  onCloseDrawer,
  onLogout,
  authRole,
}: TopNavProps) {
  const { authSession, tenants, activeTenantId, switchTenant } = useData();
  const { t, i18n } = useTranslation();

  const isAdmin = authRole === "admin" || authSession?.role === "admin";


  const [openMenu, setOpenMenu] = useState<"tenant" | "user" | "quickAdd" | null>(
    null
  );
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const closeAll = () => {
    setOpenMenu(null);
    if (isDrawerOpen) onCloseDrawer();
  };

  const quickAddItems: QuickAddItem[] = [
    { to: "/clients",              icon: "👤", title: t("topnav.quickAddItems.addClient"),      sub: t("topnav.quickAddItems.addClientSub") },
    { to: "/projects",             icon: "📁", title: t("topnav.quickAddItems.addProject"),     sub: t("topnav.quickAddItems.addProjectSub") },
    { to: "/resources",            icon: "🧰", title: t("topnav.quickAddItems.addWorker"),      sub: t("topnav.quickAddItems.addWorkerSub") },
    { to: "/subcontracts",         icon: "🧾", title: t("topnav.quickAddItems.addSubContract"), sub: t("topnav.quickAddItems.addSubContractSub") },
    { to: "/expenses",             icon: "💸", title: t("topnav.quickAddItems.addMoneyOut"),    sub: t("topnav.quickAddItems.addMoneyOutSub") },
    { to: "/payments-received",    icon: "💰", title: t("topnav.quickAddItems.addMoneyIn"),     sub: t("topnav.quickAddItems.addMoneyInSub") },
    { to: "/partner-disbursements",icon: "🤝", title: t("topnav.quickAddItems.addPartner"),     sub: t("topnav.quickAddItems.addPartnerSub") },
    { to: "/wages",                icon: "🕒", title: t("topnav.quickAddItems.addWage"),        sub: t("topnav.quickAddItems.addWageSub") },
    { to: "/invoices",             icon: "🧾", title: t("topnav.quickAddItems.addInvoice"),     sub: t("topnav.quickAddItems.addInvoiceSub") },
  ];

  return (
    <header className="topNav noPrint">
      {/* LEFT */}
      <div className="topNavLeft">
        {/* Hamburger (mobile/tablet; desktop visibility controlled by CSS) */}
        <button
          className="iconBtn topNavMenuBtn"
          onClick={onToggleDrawer}
          aria-label={t("topnav.openMenu")}
          type="button"
        >
          <span className="hamburger">
            <span />
          </span>
        </button>

        {/* Brand */}
        <Link
          to="/"
          className="topNavBrand"
          onClick={() => {
            closeAll();
          }}
        >
          <div className="topNavLogo">NJ</div>
          <div className="topNavBrandText">
            <div className="topNavTitle">{t("topnav.brand")}</div>
            <div className="topNavSub">{t("topnav.subtitle")}</div>
          </div>
        </Link>
      </div>

      {/* CENTER — Quick Access (desktop only via CSS) */}
      <div className="topNavQuickTasks">
        <Link to="/projects" className="menuBtn" onClick={closeAll}>
          <span>📁</span>
          <span className="qaLabel">{t("topnav.nav.projects")}</span>
        </Link>

        <Link to="/expenses" className="menuBtn" onClick={closeAll}>
          <span>💸</span>
          <span className="qaLabel">{t("topnav.nav.moneyOut")}</span>
        </Link>

        <Link to="/payments-received" className="menuBtn" onClick={closeAll}>
          <span>💰</span>
          <span className="qaLabel">{t("topnav.nav.moneyIn")}</span>
        </Link>

        <Link to="/wages" className="menuBtn" onClick={closeAll}>
          <span>🕒</span>
          <span className="qaLabel">{t("topnav.nav.timePay")}</span>
        </Link>

        <Link to="/schedule" className="menuBtn" onClick={closeAll}>
          <span>🗓️</span>
          <span className="qaLabel">{t("topnav.nav.schedule")}</span>
        </Link>

        <Link to="/materials" className="menuBtn" onClick={closeAll}>
          <span>🧱</span>
          <span className="qaLabel">{t("topnav.nav.materials")}</span>
        </Link>

        <Link to="/subcontracts" className="menuBtn" onClick={closeAll}>
          <span>🧾</span>
          <span className="qaLabel">{t("topnav.nav.subContract")}</span>
        </Link>

        <Link to="/invoices" className="menuBtn" onClick={closeAll}>
          <span>🧾</span>
          <span className="qaLabel">{t("topnav.nav.invoices")}</span>
        </Link>

      </div>

      {/* RIGHT */}
      <div className="topNavRight" ref={menuRef}>
        {/* Language toggle */}
        <div className="segToggle" title={t("topnav.languageToggle")}>
          <button
            type="button"
            className={`segToggleBtn${i18n.language !== "es" ? " activeLang" : ""}`}
            onClick={() => i18n.changeLanguage("en")}
          >
            EN
          </button>
          <button
            type="button"
            className={`segToggleBtn${i18n.language === "es" ? " activeLang" : ""}`}
            onClick={() => i18n.changeLanguage("es")}
          >
            ES
          </button>
        </div>

        {/* Quick Add */}
        <div className="menuWrap topNavQuickAdd">
          <button
            className={`menuBtn ${openMenu === "quickAdd" ? "menuBtnActive" : ""}`}
            onClick={() => setOpenMenu(openMenu === "quickAdd" ? null : "quickAdd")}
            aria-label={t("topnav.quickAdd")}
            type="button"
            title={t("topnav.quickAdd")}
          >
            <span style={{ fontWeight: 1000 }}>＋</span>
            <span>{t("topnav.quickAdd")}</span>
            <span style={{ opacity: 0.7, fontSize: 12 }}>▾</span>
          </button>

          {openMenu === "quickAdd" && (
            <div className="menuPanel menuPanelRight quickAddPanel">
              {quickAddItems.map((it) => (
                <Link
                  key={it.to}
                  to={it.to}
                  className="quickAddItem"
                  onClick={closeAll}
                >
                  <div className="quickAddLeft">
                    <div className="quickAddIcon" aria-hidden="true">
                      {it.icon}
                    </div>
                    <div className="quickAddText">
                      <div className="quickAddTitle">{it.title}</div>
                      <div className="quickAddSub">{it.sub}</div>
                    </div>
                  </div>

                  <div className="quickAddPlus" aria-hidden="true">
                    ＋
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Tenant switcher (admin only) */}
        {isAdmin && (
          <div className="menuWrap">
            <button
              className={`menuBtn ${
                openMenu === "tenant" ? "menuBtnActive" : ""
              }`}
              onClick={() =>
                setOpenMenu(openMenu === "tenant" ? null : "tenant")
              }
              type="button"
            >
              <span>🏢</span>
              <span className="qaLabel">
                {tenants.find((t) => t.tenantId === activeTenantId)?.tenantName ||
                  activeTenantId}
              </span>
            </button>

            {openMenu === "tenant" && (
              <div className="menuPanel menuPanelRight">
                {tenants.map((t) => (
                  <button
                    key={t.tenantId}
                    className="menuItem"
                    onClick={() => {
                      switchTenant(t.tenantId);
                      setOpenMenu(null);
                      if (isDrawerOpen) onCloseDrawer();
                    }}
                    type="button"
                  >
                    <div>
                      <div>{t.tenantName}</div>
                      <div className="menuItemSub">{t.tenantId}</div>
                    </div>
                    {t.tenantId === activeTenantId ? "✓" : ""}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* User menu */}
        <div className="menuWrap">
          <button
            className={`menuBtn ${openMenu === "user" ? "menuBtnActive" : ""}`}
            onClick={() => setOpenMenu(openMenu === "user" ? null : "user")}
            type="button"
          >
            <span>👤</span>
          </button>

          {openMenu === "user" && (
            <div className="menuPanel menuPanelRight">
              <button
                className="menuItem"
                onClick={() => {
                  setOpenMenu(null);
                  if (isDrawerOpen) onCloseDrawer();
                  onLogout();
                }}
                type="button"
              >
                <div>
                  <div>{t("topnav.logout")}</div>
                  <div className="menuItemSub">{t("topnav.logoutSub")}</div>
                </div>
                <span>⎋</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
